/**
 * React-Query hooks for `/api/admin/users/*`.
 *
 * One file groups every read + write for the admin user-management
 * surface so the page component stays focused on layout. Mutations
 * invalidate the relevant list queries on success.
 */
import {
  useMutation, useQuery, useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query'
import { api } from '@/lib/api'

// ─── Types (mirror api.schemas.admin) ───────────────────────────────

export type UserStatus = 'pending' | 'active' | 'suspended' | 'rejected'

export interface AdminUser {
  id: number
  email: string | null              // sub-accounts have null email (PULSE-47)
  role: string
  status: UserStatus
  is_active: boolean
  has_api_keys: boolean
  telegram_enabled: boolean
  bybit_testnet: boolean
  max_concurrent_positions: number
  daily_loss_limit_pct: number
  created_at: string
  last_login_at: string | null
  approved_at:  string | null
  approved_by:  number | null
  suspended_at: string | null
  rejected_at:  string | null
  // PULSE-47
  master_user_id: number | null
  account_label:  string | null
  is_master:      boolean
}

export interface AdminUserList {
  items: AdminUser[]
  total: number
  page: number
  page_size: number
}

export interface StatusCounts {
  pending: number
  active: number
  suspended: number
  rejected: number
}

// ─── Listing + counts ────────────────────────────────────────────────

interface ListParams {
  status?: UserStatus | null
  q?: string
  page?: number
  pageSize?: number
}

export function useAdminUsers(params: ListParams = {}) {
  const { status, q, page = 1, pageSize = 50 } = params
  return useQuery({
    queryKey: ['admin', 'users', { status, q, page, pageSize }],
    queryFn: async () => {
      const { data } = await api.get<AdminUserList>('/admin/users', {
        params: {
          status: status ?? undefined,
          q:      q?.trim() || undefined,
          page,
          page_size: pageSize,
        },
      })
      return data
    },
    // List moves a lot when admin actions happen — short stale time
    // so other admin tabs don't show ghost rows.
    staleTime: 5_000,
  })
}

export function useUserStatusCounts() {
  return useQuery({
    queryKey: ['admin', 'users', 'counts'],
    queryFn: async () => {
      const { data } = await api.get<StatusCounts>('/admin/users/counts')
      return data
    },
    staleTime: 5_000,
  })
}

// ─── Bybit key health of every account (PULSE-417) ──────────────────

export interface AdminBybitKey {
  user_id: number
  label: string
  is_active: boolean
  severity: 'red' | 'yellow' | 'ok' | 'pending'
  key_expires_at: string | null
  days_left: number | null
  last_check_at: string | null
  last_check_error: string | null
}

export function useAdminBybitKeys(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'bybit-keys'],
    queryFn: async () => {
      const { data } = await api.get<AdminBybitKey[]>('/admin/users/bybit-keys')
      return data
    },
    enabled,
    // Backed by the hourly probe — no point polling faster.
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
  })
}

// ─── Actions (approve / reject / suspend / reactivate) ──────────────

type ActionName = 'approve' | 'reject' | 'suspend' | 'reactivate'

function useUserAction(
  action: ActionName,
  opts?: UseMutationOptions<AdminUser, unknown, { id: number; reason?: string }>,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const { data } = await api.post<AdminUser>(
        `/admin/users/${id}/${action}`,
        { reason: reason ?? null },
      )
      return data
    },
    onSuccess(...args) {
      // Refresh list + counts; the row itself is in the response so
      // we could optimistic-update, but invalidate keeps it simple and
      // robust to multi-admin concurrent edits.
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      opts?.onSuccess?.(...args)
    },
    ...opts,
  })
}

export const useApproveUser    = (opts?: Parameters<typeof useUserAction>[1]) => useUserAction('approve',    opts)
export const useRejectUser     = (opts?: Parameters<typeof useUserAction>[1]) => useUserAction('reject',     opts)
export const useSuspendUser    = (opts?: Parameters<typeof useUserAction>[1]) => useUserAction('suspend',    opts)
export const useReactivateUser = (opts?: Parameters<typeof useUserAction>[1]) => useUserAction('reactivate', opts)

// PULSE-47 — rename a sub-account (or master).
export function useUpdateAccountLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, label }: { id: number; label: string | null }) => {
      const { data } = await api.patch<AdminUser>(
        `/admin/users/${id}/account-label`,
        { account_label: label },
      )
      return data
    },
    onSuccess() {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      // Also refresh the master's sub-account list in the topbar dropdown.
      qc.invalidateQueries({ queryKey: ['auth', 'sub-accounts'] })
    },
  })
}

// ─── PULSE-269: админский сброс пароля (временный пароль в ответе) ───

export function useAdminResetPassword() {
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const { data } = await api.post<{ ok: boolean; temp_password: string }>(
        `/admin/users/${id}/reset-password`,
      )
      return data
    },
  })
}
